import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import useDraft from "@/lib/useDraft";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, Building2, Mail, Phone, MapPin, ExternalLink, DownloadCloud, Archive, Link2 } from "lucide-react";
import ClientLogo from "@/components/ClientLogo";

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin";
  return isAdmin ? <AdminClients /> : <TeamClients userTeam={user?.team || ""} />;
}

// ============================================================
// Team member view — initials only, filtered to assigned team
// ============================================================
function TeamClients({ userTeam }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clients/visible")
      .then(({ data }) => setClients(data || []))
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="label-mono">DIRECTORY</div>
        <h1 className="font-heading text-4xl font-black tracking-tight">Clients</h1>
        <p className="text-sm text-zinc-600 mt-1 font-mono">
          {userTeam ? `Clients assigned to team "${userTeam}"` : "Your team's clients"} · initials only
        </p>
      </div>

      {loading && <div className="font-mono text-xs text-zinc-500">Loading…</div>}
      {!loading && clients.length === 0 && (
        <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm">
          NO CLIENTS ASSIGNED TO YOUR TEAM YET — ASK YOUR ADMIN TO ASSIGN ONE
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {clients.map((c) => <ClientInitialTile key={c.id} c={c} />)}
      </div>
    </div>
  );
}

function ClientInitialTile({ c }) {
  return (
    <div className="brut-card p-4 flex flex-col items-center justify-center text-center min-h-[140px]" data-testid={`client-tile-${c.id}`}>
      <div className="w-16 h-16 border border-zinc-900 bg-yellow-50 flex items-center justify-center overflow-hidden mb-2">
        <ClientLogo client={c} alt={c.initials} />
      </div>
      <div className="font-heading text-2xl font-black tracking-tight" data-testid={`client-initials-${c.id}`}>{c.initials || "—"}</div>
      {Array.isArray(c.assigned_teams) && c.assigned_teams.length > 0 && (
        <div className="label-mono text-[10px] mt-1 text-zinc-500 truncate w-full" title={c.assigned_teams.join(", ")}>
          {c.assigned_teams.join(" · ")}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Admin view — full directory + create/edit modal
// ============================================================
function AdminClients() {
  const [clients, setClients] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filterOrgId, setFilterOrgId] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [unlinked, setUnlinked] = useState(null);

  const load = async () => {
    const [{ data: c }, { data: o }, { data: u }] = await Promise.all([
      api.get("/clients", { params: { include_archived: true } }),
      api.get("/organizations").catch(() => ({ data: [] })),
      api.get("/jobs/unlinked").catch(() => ({ data: null })),
    ]);
    setClients(c || []);
    setOrgs(o || []);
    setUnlinked(u);
  };
  useEffect(() => { load(); }, []);

  const orgById = React.useMemo(() => {
    const m = {};
    for (const o of orgs) m[o.id] = o;
    return m;
  }, [orgs]);

  const remove = async (c) => {
    if (!confirm(`Delete client "${c.name}"?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Client deleted");
    load();
  };

  const toggleArchive = async (c) => {
    try {
      await api.post(`/clients/${c.id}/${c.archived ? "unarchive" : "archive"}`);
      toast.success(c.archived ? "Client restored" : "Client archived — history kept, hidden from lists");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };

  const toggleCheck = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    if (!confirm(`Delete ${checked.size} selected client(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/clients/bulk-delete", { ids: [...checked] });
      toast.success(`Deleted ${data.deleted} client(s)`);
      setChecked(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
    setBulkDeleting(false);
  };

  const cleanupNonSD = async () => {
    try {
      const { data: dry } = await api.post("/clients/cleanup-non-sd", { dry_run: true });
      if (!dry.count) { toast.success("No junk clients found — directory is clean"); return; }
      if (!confirm(`Remove ${dry.count} auto-created (non-SD, non-manual) client(s)?\n\nExamples: ${dry.clients.slice(0, 8).join(", ")}${dry.count > 8 ? "…" : ""}\n\nJobs keep their client names; only the junk client records are removed. They will NOT come back on the next sync.`)) return;
      const { data } = await api.post("/clients/cleanup-non-sd", {});
      toast.success(`Removed ${data.deleted} junk client(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
  };

  const importFromSD = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/integrations/surveydrafts/import-clients", {});
      toast.success(`SD import: ${data.created} created · ${data.updated} updated (${data.fetched} fetched)`, { duration: 6000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "SD import failed"); }
    setImporting(false);
  };

  // Group + sort by org
  const visibleClients = React.useMemo(() => {
    let filtered = filterOrgId ? clients.filter((c) => c.org_id === filterOrgId) : clients;
    filtered = showArchived ? filtered : filtered.filter((c) => !c.archived);
    return [...filtered].sort((a, b) => {
      const ao = orgById[a.org_id]?.short_code || orgById[a.org_id]?.name || "zzz";
      const bo = orgById[b.org_id]?.short_code || orgById[b.org_id]?.name || "zzz";
      const ocmp = ao.localeCompare(bo);
      if (ocmp !== 0) return ocmp;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [clients, filterOrgId, orgById, showArchived]);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">DIRECTORY</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Clients</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">{visibleClients.length} clients{filterOrgId ? ` in ${orgById[filterOrgId]?.short_code || orgById[filterOrgId]?.name || "selected org"}` : ` across ${orgs.length} orgs`}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          {checked.size > 0 && (
            <button onClick={bulkDelete} disabled={bulkDeleting} className="brut-btn px-4 py-2 text-sm flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60" data-testid="bulk-delete-clients-btn">
              <Trash2 className="w-4 h-4" /> {bulkDeleting ? "DELETING…" : `DELETE SELECTED (${checked.size})`}
            </button>
          )}
          <label className="flex items-center gap-1.5 text-xs font-mono cursor-pointer border border-zinc-900 px-3 py-2 bg-white">
            <input type="checkbox" className="w-3.5 h-3.5 accent-zinc-900" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived-toggle" />
            ARCHIVED ({clients.filter((c) => c.archived).length})
          </label>
          <button onClick={cleanupNonSD} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2 border-red-700 text-red-700 hover:bg-red-50" data-testid="cleanup-non-sd-btn" title="Remove clients auto-created from job names — keeps SD-imported + manually created clients">
            <Trash2 className="w-4 h-4" /> REMOVE NON-SD
          </button>
          <button onClick={() => setShowRates(true)} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2 border-amber-700 text-amber-800 hover:bg-amber-50" data-testid="bulk-rates-btn" title="Set per-hour / per-project rates for many clients at once">
            <Pencil className="w-4 h-4" /> EDIT RATES
          </button>
          <button onClick={importFromSD} disabled={importing} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50 disabled:opacity-60" data-testid="import-sd-clients-btn" title="Import all clients (name + initials) from surveydrafters.us">
            <DownloadCloud className="w-4 h-4" /> {importing ? "IMPORTING…" : "IMPORT FROM SD"}
          </button>
          {orgs.length > 0 && (
            <div>
              <div className="label-mono mb-1">FILTER · ORGANIZATION</div>
              <select
                value={filterOrgId}
                onChange={(e) => setFilterOrgId(e.target.value)}
                className="brut-input px-3 py-2 text-sm font-mono min-w-[200px]"
                data-testid="clients-filter-org"
              >
                <option value="">All organizations ({clients.length})</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.short_code || o.name} ({clients.filter((c) => c.org_id === o.id).length})
                  </option>
                ))}
              </select>
            </div>
          )}
          <button onClick={() => setShowNew(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="add-client-btn">
            <Plus className="w-4 h-4" /> NEW CLIENT
          </button>
        </div>
      </div>

      {unlinked && unlinked.total > 0 && (
        <UnlinkedJobsCard data={unlinked} clients={clients.filter((c) => !c.archived)} onLinked={load} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleClients.length === 0 && (
          <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm lg:col-span-3">
            NO CLIENTS YET — ADD ONE TO ENABLE INVOICING WITH LOGO + AUTO-PRICING
          </div>
        )}
        {visibleClients.map((c) => (
          <ClientCard key={c.id} c={c} org={orgById[c.org_id]}
            checked={checked.has(c.id)} onCheck={() => toggleCheck(c.id)}
            onEdit={() => setEditing(c)} onDelete={() => remove(c)} onArchive={() => toggleArchive(c)} />
        ))}
      </div>

      {(showNew || editing) && (
        <ClientModal
          client={editing}
          orgs={orgs}
          defaultOrgId={filterOrgId}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
      {showRates && <BulkRatesModal clients={visibleClients} onClose={() => setShowRates(false)} onSaved={() => { setShowRates(false); load(); }} />}
    </div>
  );
}

function UnlinkedJobsCard({ data, clients, onLinked }) {
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);

  const link = async (name) => {
    const clientId = sel[name];
    if (!clientId) { toast.error("Pick a client to link to"); return; }
    setBusy(name);
    try {
      const { data: res } = await api.post("/jobs/link-client", { client_name: name, client_id: clientId });
      toast.success(`Linked ${res.linked} job(s) to ${res.client_name}`);
      onLinked();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
    setBusy(null);
  };

  return (
    <div className="brut-card p-4 border-orange-600" data-testid="unlinked-jobs-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="unlinked-jobs-toggle">
        <div className="label-mono flex items-center gap-2 text-orange-800">
          <Link2 className="w-3.5 h-3.5" /> UNLINKED JOBS — {data.total} job(s) across {data.groups.length} client name(s) not matched to any client
        </div>
        <span className="font-mono text-xs">{open ? "▲ HIDE" : "▼ REVIEW"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
          {data.groups.map((g) => (
            <div key={g.client_name} className="flex flex-wrap items-center gap-2 border border-zinc-300 p-2" data-testid={`unlinked-group-${g.client_name}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-mono text-xs font-bold">{g.client_name}</div>
                <div className="font-mono text-[10px] text-zinc-500">{g.count} job(s) · e.g. {g.sample_jobs.join(", ") || "—"}</div>
              </div>
              <select className="brut-input px-2 py-1 text-xs font-mono max-w-[220px]" value={sel[g.client_name] || ""} onChange={(e) => setSel({ ...sel, [g.client_name]: e.target.value })}>
                <option value="">— link to client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={() => link(g.client_name)} disabled={busy === g.client_name} className="brut-btn px-3 py-1 text-xs disabled:opacity-60" data-testid={`link-jobs-btn-${g.client_name}`}>
                {busy === g.client_name ? "LINKING…" : "LINK"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ c, org, checked, onCheck, onEdit, onDelete, onArchive }) {
  return (
    <div className={`brut-card p-4 ${checked ? "ring-2 ring-red-500" : ""} ${c.archived ? "opacity-60 bg-zinc-100" : ""}`} data-testid={`client-card-${c.id}`}>
      {c.archived && <div className="label-mono text-[9px] inline-block px-1.5 py-0.5 border border-zinc-500 bg-zinc-200 text-zinc-600 mb-2" data-testid={`archived-badge-${c.id}`}>ARCHIVED</div>}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 w-4 h-4 accent-red-600 cursor-pointer flex-shrink-0"
          checked={!!checked}
          onChange={onCheck}
          title="Select for bulk delete"
          data-testid={`select-client-${c.id}`}
        />
        <div className="w-16 h-16 border border-zinc-900 bg-zinc-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <ClientLogo client={c} alt={c.name} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-lg font-bold truncate">{c.name}</div>
            {c.initials && (
              <span className="font-mono text-xs font-bold bg-yellow-200 border border-zinc-900 px-1.5 py-0.5" data-testid={`admin-client-initials-${c.id}`}>{c.initials}</span>
            )}
            {org && (
              <span className="font-mono text-[10px] font-bold bg-green-100 border border-zinc-900 px-1.5 py-0.5" data-testid={`admin-client-org-${c.id}`} title={org.name}>
                {org.short_code || org.name}
              </span>
            )}
          </div>
          {c.contact_person && <div className="text-xs text-zinc-600 truncate">{c.contact_person}</div>}
          {c.team && <span className="inline-block label-mono mt-1 px-1.5 py-0.5 bg-zinc-100 border border-zinc-900">{c.team}</span>}
          {Array.isArray(c.assigned_teams) && c.assigned_teams.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {c.assigned_teams.map((t) => (
                <span key={t} className="label-mono px-1.5 py-0.5 bg-blue-50 border border-zinc-900">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1 text-xs font-mono">
        {c.email && <div className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 flex-shrink-0" />{c.email}</div>}
        {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone}</div>}
        {c.address && <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /><span className="truncate">{c.address}</span></div>}
      </div>

      <div className="mt-3 border-t border-zinc-200 pt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="label-mono">PER JOB</div>
          <div className="font-mono font-bold">{c.currency || "INR"} {c.default_rate_per_job || 0}</div>
        </div>
        <div>
          <div className="label-mono">PER HOUR</div>
          <div className="font-mono font-bold">{c.currency || "INR"} {c.default_rate_per_hour || 0}</div>
        </div>
        <div>
          <div className="label-mono">ACTIVE JOBS</div>
          <div className="font-mono font-bold">{c.active_job_count}</div>
        </div>
        <div>
          <div className="label-mono">TOTAL JOBS</div>
          <div className="font-mono font-bold">{c.job_count}</div>
        </div>
      </div>

      <div className="flex justify-end gap-1 mt-3">
        <Link to={`/clients/${c.id}`} className="brut-btn-secondary px-2.5 py-1.5 text-xs flex items-center gap-1" data-testid={`view-client-${c.id}`}>
          <ExternalLink className="w-3 h-3" /> VIEW
        </Link>
        <button onClick={onEdit} className="brut-btn-secondary px-2.5 py-1.5 text-xs flex items-center gap-1" data-testid={`edit-client-${c.id}`}>
          <Pencil className="w-3 h-3" /> EDIT
        </button>
        <button onClick={onArchive} className={`brut-btn-secondary px-2.5 py-1.5 text-xs flex items-center gap-1 ${c.archived ? "border-green-700 text-green-800 hover:bg-green-50" : "border-zinc-500 text-zinc-600 hover:bg-zinc-100"}`} title={c.archived ? "Restore client" : "Archive — hide but keep history"} data-testid={`archive-client-${c.id}`}>
          <Archive className="w-3 h-3" /> {c.archived ? "RESTORE" : "ARCHIVE"}
        </button>
        <button onClick={onDelete} className="text-red-600 p-1.5" title="Delete permanently"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function ClientModal({ client, orgs = [], defaultOrgId = "", onClose, onSaved }) {
  // Key drafts by updated_at so stale drafts from before the last save never
  // shadow fresh server values (was: edits appeared to "not save")
  const draftKey = client ? `${client.id}:${client.updated_at || "0"}` : "new";
  const [form, setForm, clearDraft] = useDraft("client", draftKey, client || {
    name: "", initials: "", assigned_teams: [],
    contact_person: "", email: "", phone: "", address: "", billing_address: "",
    gst_number: "", default_rate_per_hour: 0, default_rate_per_job: 0, currency: "USD", team: "", notes: "",
    default_template_id: "",
    org_id: defaultOrgId || "",
  });
  const [teamInput, setTeamInput] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get("/invoice-templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
    api.get("/team/teams").then(({ data }) => setAvailableTeams((data?.teams || []).map((t) => t.team))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let saved;
      if (client) {
        const { data } = await api.patch(`/clients/${client.id}`, form);
        saved = data;
      } else {
        const { data } = await api.post("/clients", form);
        saved = data;
      }
      if (logoFile) {
        const fd = new FormData(); fd.append("file", logoFile);
        await api.post(`/clients/${saved.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(client ? "Client updated" : "Client created");
      clearDraft();
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="brut-card max-w-2xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-white shrink-0">
          <div>
            <h2 className="font-heading text-xl font-black">{client ? "Edit Client" : "New Client"}</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5" data-testid="client-draft-hint">
              DRAFT AUTO-SAVED · Outside-click won't dismiss
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { if (confirm("Discard unsaved draft for this form?")) { clearDraft(); onClose(); } }}
              className="brut-btn-secondary px-2 py-1 text-[10px] font-mono"
              title="Discard the auto-saved draft and close"
              data-testid="client-discard-draft"
            >DISCARD</button>
            <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <form id="client-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 px-5 py-4 overflow-y-auto flex-1 min-h-0">
          <Field label="COMPANY NAME" required full>
            <input required className="brut-input w-full px-3 py-2 text-sm" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
          </Field>
          <Field label="ORGANIZATION" required>
            <select
              required
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              value={form.org_id ?? ""}
              onChange={(e) => setForm({ ...form, org_id: e.target.value })}
              data-testid="client-org-select"
            >
              <option value="">— Select org —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.short_code || o.name}</option>
              ))}
            </select>
            <div className="label-mono mt-1 text-zinc-500">Clients are grouped + filtered by organization across the app.</div>
          </Field>
          <Field label="INITIALS / CODE (SHOWN TO TEAMS)" required>
            <input required maxLength={8} className="brut-input w-full px-3 py-2 text-sm font-mono uppercase"
              value={form.initials ?? ""}
              onChange={(e) => setForm({ ...form, initials: e.target.value.toUpperCase() })}
              placeholder="e.g. ACME"
              data-testid="client-initials-input" />
            <div className="label-mono mt-1 text-zinc-500">Team members see this short code instead of the full company name.</div>
          </Field>
          <Field label="ASSIGNED TEAMS" full>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {(form.assigned_teams || []).map((t) => (
                <span key={t} className="font-mono text-xs bg-blue-100 border border-zinc-900 px-2 py-1 flex items-center gap-1">
                  {t}
                  <button type="button" onClick={() => setForm({ ...form, assigned_teams: (form.assigned_teams || []).filter((x) => x !== t) })} className="text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {(form.assigned_teams || []).length === 0 && (
                <span className="label-mono text-zinc-400">No teams assigned — only admins will see this client</span>
              )}
            </div>
            <div className="flex gap-2">
              <select
                className="brut-input flex-1 px-3 py-1.5 text-sm font-mono"
                value={teamInput}
                onChange={(e) => setTeamInput(e.target.value)}
                data-testid="client-team-select"
              >
                <option value="">— Select an existing team —</option>
                {availableTeams
                  .filter((t) => !(form.assigned_teams || []).includes(t))
                  .map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
              </select>
              <button type="button"
                onClick={() => {
                  const v = teamInput.trim();
                  if (v && !(form.assigned_teams || []).includes(v)) {
                    setForm({ ...form, assigned_teams: [...(form.assigned_teams || []), v] });
                  }
                  setTeamInput("");
                }}
                className="brut-btn-secondary px-3 py-1.5 text-xs"
                data-testid="client-team-add-btn">ADD TEAM</button>
            </div>
            <div className="label-mono mt-1 text-zinc-500">Pick from teams configured in <strong>Attendance → Employees</strong>. Members of these teams will see this client.</div>
          </Field>
          <Field label="CONTACT PERSON">
            <input className="brut-input w-full px-3 py-2 text-sm" value={form.contact_person ?? ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          </Field>
          <Field label="PRIMARY TEAM">
            <select
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              value={form.team ?? ""}
              onChange={(e) => setForm({ ...form, team: e.target.value })}
              data-testid="client-primary-team-select"
            >
              <option value="">— None —</option>
              {availableTeams.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="EMAIL">
            <input type="email" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="PHONE">
            <input className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="ADDRESS" full>
            <textarea rows={2} className="brut-input w-full px-3 py-2 text-sm" value={form.address ?? ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="BILLING ADDRESS (IF DIFFERENT)" full>
            <textarea rows={2} className="brut-input w-full px-3 py-2 text-sm" value={form.billing_address ?? ""} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
          </Field>
          <Field label="GST / TAX NUMBER">
            <input className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.gst_number ?? ""} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} />
          </Field>
          <Field label="CURRENCY">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.currency ?? "USD"} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="USD">USD ($)</option>
              <option value="INR">INR (₹)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </Field>
          <Field label="DEFAULT RATE PER JOB">
            <input type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.default_rate_per_job ?? 0} onChange={(e) => setForm({ ...form, default_rate_per_job: parseFloat(e.target.value) || 0 })} data-testid="rate-per-job-input" />
          </Field>
          <Field label="DEFAULT RATE PER HOUR">
            <input type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.default_rate_per_hour ?? 0} onChange={(e) => setForm({ ...form, default_rate_per_hour: parseFloat(e.target.value) || 0 })} />
          </Field>
          <Field label="DEFAULT INVOICE TEMPLATE">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.default_template_id ?? ""} onChange={(e) => setForm({ ...form, default_template_id: e.target.value })} data-testid="client-template-select">
              <option value="">— Use org default —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="LOGO (PNG/JPG)" full>
            <input ref={logoRef} type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="brut-input w-full px-3 py-2 text-sm" />
            {logoFile && <div className="label-mono mt-1">Selected: {logoFile.name}</div>}
          </Field>
          <Field label="NOTES" full>
            <textarea rows={2} className="brut-input w-full px-3 py-2 text-sm" value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </form>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-900 bg-white shrink-0">
          <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button type="submit" form="client-form" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-50" data-testid="save-client-btn">{saving ? "SAVING..." : (client ? "SAVE" : "CREATE CLIENT")}</button>
        </div>
      </div>
    </div>
  ), document.body);
}

function BulkRatesModal({ clients, onClose, onSaved }) {
  const [rows, setRows] = useState(() => clients.map((c) => ({
    id: c.id, name: c.name, initials: c.initials || "",
    currency: c.currency || "USD",
    default_rate_per_job: c.default_rate_per_job || 0,
    default_rate_per_hour: c.default_rate_per_hour || 0,
  })));
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const upd = (id, key, value) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const saveAll = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/clients/bulk-rates", { updates: rows });
      toast.success(`Rates updated for ${data.updated} client(s)`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const visible = filter ? rows.filter((r) => (r.name + r.initials).toLowerCase().includes(filter.toLowerCase())) : rows;
  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 shrink-0">
          <div>
            <h2 className="font-heading text-xl font-black">Bulk Edit Rates</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">Per-project (flat) price wins over per-hour on invoice imports</div>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 pt-3 shrink-0">
          <input placeholder="Filter clients…" className="brut-input w-full px-3 py-2 text-sm font-mono" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="rates-filter-input" />
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-3">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0">
              <tr className="bg-zinc-900 text-white">
                <th className="text-left px-2 py-2 label-mono text-white">Client</th>
                <th className="text-left px-2 py-2 label-mono text-white">Currency</th>
                <th className="text-left px-2 py-2 label-mono text-white">Per Project (flat)</th>
                <th className="text-left px-2 py-2 label-mono text-white">Per Hour</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-zinc-200" data-testid={`rate-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-mono">{r.name}{r.initials ? ` · ${r.initials}` : ""}</td>
                  <td className="px-2 py-1.5">
                    <select className="brut-input px-2 py-1 text-xs font-mono" value={r.currency} onChange={(e) => upd(r.id, "currency", e.target.value)}>
                      <option>USD</option><option>INR</option><option>EUR</option><option>GBP</option>
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" step="0.01" className="brut-input w-24 px-2 py-1 text-xs font-mono" value={r.default_rate_per_job} onChange={(e) => upd(r.id, "default_rate_per_job", parseFloat(e.target.value) || 0)} data-testid={`rate-job-${r.id}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" step="0.01" className="brut-input w-24 px-2 py-1 text-xs font-mono" value={r.default_rate_per_hour} onChange={(e) => upd(r.id, "default_rate_per_hour", parseFloat(e.target.value) || 0)} data-testid={`rate-hour-${r.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-900 shrink-0">
          <button onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button onClick={saveAll} disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-60" data-testid="save-rates-btn">{saving ? "SAVING…" : `SAVE ALL (${rows.length})`}</button>
        </div>
      </div>
    </div>
  ), document.body);
}

const Field = ({ label, children, full, required }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <div className="label-mono mb-1">{label}{required && <span className="text-red-600"> *</span>}</div>
    {children}
  </label>
);
